import React, { useState, useContext } from "react";
import axios from "axios";
import moment from "moment";
import DataTable from "react-data-table-component";
import { NotificationManager } from "react-notifications";
import { makeStyles } from "@mui/styles";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import CheckIcon from '@mui/icons-material/Check';
import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";
import { deleteMarketMembershipClaim, downloadFile } from "services/ConfigServices";
import useClaimTable from "./useClaimTable.js";
import EditClaimDialog from "./EditClaimDialog";
import EditClaimStatusDialog from "./EditClaimStatusDialog";
import ValidateClaimDialog from "./ValidateClaimDialog";
import CustomDeleteButton from "components/CustomDeleteButton/CustomDeleteButton.js";
import UserService from "services/UserService";
import Button from "components/CustomButtons/Button.js";
import styled from "styled-components";
import AdvancedSearchContext from "context/AdvancedSearchContext";
import { memberclaim } from "variables/markets";
import { memberclaimStatusData } from "variables/lists/markets";
import Dialog from "@mui/material/Dialog";
import { withStyles } from "@mui/styles";
import MuiDialogContent from "@mui/material/DialogContent";
import MuiDialogActions from "@mui/material/DialogActions";
import CustomDialogTitle from "components/Dialog/CustomDialogTitle.js";
import MarketsContext from "context/MarketsContext";


const GroupCell = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const Container = styled.div`
  border-bottom: 0.5px solid rgba(0, 0, 0, 0.15);
  border-top: 4px solid rgba(255, 255, 255, 1);
  background-color: white;
  margin-top: -0.5px;
`;

const ExpandableContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.5rem 4rem 2rem;
`;
const ExpandableElement = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: ${(props) => props.marginleft};
`;

const ElementTitle = styled.div`
  font-weight: 600;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
`;

const ElementData = styled.div`
  margin-left: 4px;
  font-weight: 400;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
`;

const ElementSectionTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  font-family: "Source Sans Pro",sans-serif;
  text-decoration: underline;
  margin-top: 10px;
`;

const NoData = styled.p`
  font-size: 14px;
  font-weight: 600;
  margin-top: 20px;
  opacity: 1;
`;

const ElementDataDownload = styled.div`
  margin-left: 4px;
  font-weight: 400;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
  cursor: pointer;
  text-decoration: underline;
`;


const DialogContent = withStyles((theme) => ({
  root: {
    padding: '16px',
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: '8px',
  },
}))(MuiDialogActions);

const ExpandableComponent = props => {
  const { data } = props;
  const marketContext = useContext(MarketsContext);
  const { market, setMarket } = marketContext;
  const detailarr = data.parameters?.detail?.split("\n") || [];
  const rejectioncausearr = data.parameters?.rejectioncause?.split("\n") || [];
  return (
    <Container>
      <ExpandableContainer>
        <ElementSectionTitle>Réclamation</ElementSectionTitle>
        <ExpandableElement>
          <ElementTitle>{memberclaim.marketmembershipnumber} : </ElementTitle>
          <ElementData>{data.parameters?.marketmembershipnumber}</ElementData>
        </ExpandableElement>
        <ExpandableElement>
          <ElementTitle>{memberclaim.marketmembershipname} : </ElementTitle>
          <ElementData>{data.parameters?.marketmembershipname}</ElementData>
        </ExpandableElement>
        <ExpandableElement>
          <ElementTitle>{memberclaim.author} : </ElementTitle>
          <ElementData>{data.parameters?.author}</ElementData>
        </ExpandableElement>
        <ExpandableElement>
          <ElementTitle>{memberclaim.creationdate} : </ElementTitle>
          <ElementData>{data.parameters.creationdate && moment(data.parameters.creationdate).isValid()
            ? moment(data.parameters.creationdate).format("DD/MM/YYYY HH:mm")
            : ""}</ElementData>
        </ExpandableElement>
        <ExpandableElement>
          <ElementTitle>{memberclaim.purpose} : </ElementTitle>
          <ElementData>{data.parameters?.purpose}</ElementData>
        </ExpandableElement>
        <ExpandableElement>
          <ElementTitle>{memberclaim.detail} : </ElementTitle>
          <ElementData>{detailarr.map((line) => {
            return (
              <>
                {line}<br />
              </>
            );
          })
          }</ElementData>
        </ExpandableElement>
        <ExpandableElement>
          <ElementTitle>{memberclaim.amountasked} : </ElementTitle>
          <ElementData>{data.parameters?.amountasked && new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(data.parameters?.amountasked)} €</ElementData>
        </ExpandableElement>
        <ExpandableElement>
          <ElementTitle>{memberclaim.associatedfiles} : </ElementTitle>
          {data.parameters?.associatedfiles.map((file) => {
            return <ElementDataDownload onClick={downloadGED(file.fileId, file.fileName)}>{file.fileName}</ElementDataDownload>;
          })
          }
        </ExpandableElement>
        <ElementSectionTitle>Etat d'avancement</ElementSectionTitle>
        <ExpandableElement>
          <ElementTitle>{memberclaim.status} : </ElementTitle>
          <ElementData>{data.parameters?.status && <div style={{ color: memberclaimStatusData.find((x) => x.id == data.parameters.status).color, fontWeight: "bold" }}>
            {memberclaimStatusData.find((x) => x.id == data.parameters.status).name}</div>}
          </ElementData>
        </ExpandableElement>
        {
          data.parameters?.status === 'refused' &&
          <ExpandableElement>
            <ElementTitle>{memberclaim.rejectioncause} : </ElementTitle>
            <ElementData>{rejectioncausearr.map((line) => {
            return (
              <>
                {line}<br />
              </>
            );
          })
          }</ElementData>
          </ExpandableElement>
        }
        {
          data.parameters?.status === 'payed' &&
          <ExpandableElement>
            <ElementTitle>{memberclaim.amountreceived} : </ElementTitle>
            <ElementData>{data.parameters?.amountreceived && new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(data.parameters?.amountreceived)} €</ElementData>
          </ExpandableElement>
        }
        {
          (data.parameters?.status === 'refused' || data.parameters?.status === 'payed') &&
          <ExpandableElement>
            <ElementTitle>{memberclaim.enddate} : </ElementTitle>
            <ElementData>{data.parameters.enddate && moment(data.parameters.enddate).isValid()
              ? moment(data.parameters.enddate).format("DD/MM/YYYY hh:mm")
              : ""}</ElementData>
          </ExpandableElement>
        }
      </ExpandableContainer>
    </Container >
  );
};

const downloadGED = (fileId, fileName) => async () => {
  axios
    .get(
      downloadFile(fileId),
      {
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${UserService.getToken()}`,
        },
      }
    )
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName); //or any other extension
      document.body.appendChild(link);
      link.click();
    });
};


const useStyles = makeStyles(styles);

const RTClaims = () => {
  const permis = JSON.parse(localStorage.getItem("permission")) || [];
  const markets_update = permis.includes("markets_update");
  const markets_admin = permis.includes("markets_admin");
  const { gridState, refetch, setSelectedClaims } = useClaimTable();
  const marketContext = useContext(MarketsContext);
  const { market, setMarket } = marketContext;
  const advancedSearchContext = useContext(AdvancedSearchContext);
  const { groupInfoListById, setPage } = advancedSearchContext;
  const [open, setOpen] = useState(false);
  const [openStatus, setOpenStatus] = useState(false);
  const [openValidateClaim, setOpenValidateClaim] = useState(false);
  const [currentClaim, setCurrentClaim] = useState(null);

  let dataClaims = gridState.data;

  const classes = useStyles();

  const columns = [
    {
      name: memberclaim.purpose,
      selector: (row) => row.parameters.purpose,
      sortable: true,
      maxWidth: "13rem",
    },
    {
      name: memberclaim.amountasked,
      selector: (row) => row.parameters.amountasked,
      sortable: true,
      maxWidth: "10rem",
      cell: (row) =>
        <>{row.parameters?.amountasked && new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(row.parameters?.amountasked)} €</>
    },
    {
      name: memberclaim.author,
      selector: (row) => row.parameters.author,
      sortable: true,
      maxWidth: "13rem",
      cell: (row) =>
        <div>
          {row.parameters.author}
        </div>
    },
    {
      name: memberclaim.marketmembershipnumber,
      selector: (row) => row.marketmembershipnumber,
      sortable: true,
      maxWidth: "9rem",
    },
    {
      name: memberclaim.marketmembershipname,
      selector: (row) => row.marketmembershipname,
      sortable: true,
      maxWidth: "9rem",
    },
    {
      name: memberclaim.creationdate,
      selector: (row) => row.parameters.creationdate,
      sortable: true,
      maxWidth: "10rem",
      cell: (row) =>
        row.parameters.creationdate && moment(row.parameters.creationdate).isValid()
          ? moment(row.parameters.creationdate).format("DD/MM/YYYY HH:mm")
          : "",
    },
    {
      name: memberclaim.status,
      selector: (row) => row.parameters.status,
      sortable: true,
      maxWidth: "12rem",
      cell: (row) => row.parameters.status ?
        <div style={{ color: memberclaimStatusData.find((x) => x.id == row.parameters.status).color, fontWeight: "bold" }}>
          {memberclaimStatusData.find((x) => x.id == row.parameters.status).name}
        </div> : <></>
    },
    {
      name: memberclaim.enddate,
      selector: (row) => row.parameters.startdate ? row.parameters.startdate : row.parameters.enddate,
      sortable: true,
      maxWidth: "10rem",
      cell: (row) =>
        row.parameters.enddate && moment(row.parameters.enddate).isValid()
          ? moment(row.parameters.enddate).format("DD/MM/YYYY")
          : "",
    },
    {
      name: "",
      sortable: false,
      width: "10rem",
      // eslint-disable-next-line react/button-has-type
      // eslint-disable-next-line react/display-name
      cell: (row) => (
        <>
          <div className="actions-right">
            {
              markets_update && row.parameters.status === 'membervalidation' &&
              <Tooltip title="Valider la réclamation">
                <Button
                  onClick={validateClaim(row)}
                  color={"success"}
                  className={classes.actionButton}
                >
                  <CheckIcon fontSize="large" />
                </Button>
              </Tooltip>
            }
            {
              markets_admin && row.parameters.status !== 'membervalidation' &&
              <Tooltip title="Changer le statut">
                <Button
                  onClick={editClaimStatus(row)}
                  color={"info"}
                  className={classes.actionButton}
                >
                  <ExitToAppIcon fontSize="large" />
                </Button>
              </Tooltip>
            }
            {markets_update && row.parameters.status === 'membervalidation' &&
              <Tooltip title="Editer la réclamation'">
                <Button
                  onClick={editClaim(row)}
                  color={"warning"}
                  className={classes.actionButton}
                >
                  <EditIcon fontSize="large" />
                </Button>
              </Tooltip>
            }
            {((markets_update && row.parameters.status === 'membervalidation') || (markets_admin)) &&
              <Tooltip title="Supprimer la réclamation'">
                <CustomDeleteButton
                  deleteTitle={"Suppression de la réclamation"}
                  deleteSubTitle={`Êtes-vous sûr de vouloir supprimer la réclamation?`}
                  handleSupprimer={deleteClaimFunction(row)}
                />
              </Tooltip>
            }
          </div>
        </>
      ),
    },
  ];

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleEditClose = () => {
    setOpen(false);
  };

  const handleClickStatusOpen = () => {
    setOpenStatus(true);
  };
  const handleEditStatusClose = () => {
    setOpenStatus(false);
  };

  const handleClickValidateClaimOpen = () => {
    setOpenValidateClaim(true);
  };
  const handleClickValidateClaimClose = () => {
    setOpenValidateClaim(false);
  };

  const editClaim = (row) => async () => {
    let obj = dataClaims.find((o) => o.id === row.id);
    if (obj) {
      setCurrentClaim(obj);
      handleClickOpen();
    }
  }

  const editClaimStatus = (row) => async () => {
    let obj = dataClaims.find((o) => o.id === row.id);
    if (obj) {
      setCurrentClaim(obj);
      handleClickStatusOpen();
    }
  }

  const validateClaim = (row) => async () => {
    let obj = dataClaims.find((o) => o.id === row.id);
    console.log(obj);
    if (obj) {
      setCurrentClaim(obj);
      handleClickValidateClaimOpen();
    }
  }

  const deleteClaimFunction = (row) => async () => {
    let obj = dataClaims.find((o) => o.id === row.id);
    let idSelected = obj.id;
    let url = deleteMarketMembershipClaim(idSelected);
    axios
      .delete(url, {
        headers: {
          Authorization: `Bearer ${UserService.getToken()}`,
        },
      })
      .then((res) => {
        if (res.status == 200) {
          setNotification(
            "success",
            `La réclamation a été supprimée`
          );
          refetch();
        } else setNotification("error", "Une erreur est survenue");
      })
      .catch((error) => {
        setNotification("error", "Une erreur est survenue");
      });
  };

  const setNotification = (status, text) => {
    if (status === "success") {
      NotificationManager.success(text);
    } else {
      NotificationManager.error(text);
    }
  };

  const handleSelectedRowsChange = (selection) => {
    setSelectedClaims(selection.selectedRows);
  };

  return (
    <>
      <DataTable
          paginationRowsPerPageOptions={[10,20,30,50,100]}
        noDataComponent={<NoData>Aucune réclamation à afficher</NoData>}
        noHeader={true}
        columns={columns}
        persistTableHead
        pagination
        subHeader
        progressPending={gridState.loading}
        progressComponent={<LinearProgress />}
        onSelectedRowsChange={handleSelectedRowsChange}
        selectableRows={true}
        expandOnRowClicked
        expandableRows
        expandableRowDisabled={(row) => row.disabled}
        expandableRowsComponent={ExpandableComponent}
        paginationComponentOptions={{
          noRowsPerPage: false,
          rowsPerPageText: "Lignes par page",
          rangeSeparatorText: "sur",
          selectAllRowsItem: false,
          selectAllRowsItemText: "Selectionner toutes les lignes",
        }}
        {...gridState}
      />
      <Dialog
        onClose={handleEditClose}
        aria-labelledby="edituser-dialog-title"
        fullWidth="true"
        maxWidth="sm"
        open={open}
      >
        <CustomDialogTitle id="customized-dialog-title" onClose={handleEditClose}>
          Modification de la réclamation
        </CustomDialogTitle>
        <DialogContent dividers>
          <EditClaimDialog
            claim={currentClaim}
            closeModal={handleEditClose}
            refetch={refetch}
          />
        </DialogContent>
        <DialogActions></DialogActions>
      </Dialog>
      <Dialog
        onClose={handleEditStatusClose}
        aria-labelledby="edituser-dialog-title"
        fullWidth="true"
        maxWidth="sm"
        open={openStatus}
      >
        <CustomDialogTitle id="customized-dialog-title" onClose={handleEditStatusClose}>
          Modification de la réclamation
        </CustomDialogTitle>
        <DialogContent dividers>
          <EditClaimStatusDialog
            claim={currentClaim}
            closeModal={handleEditStatusClose}
            refetch={refetch}
          />
        </DialogContent>
        <DialogActions></DialogActions>
      </Dialog>
      <Dialog
        onClose={handleEditStatusClose}
        aria-labelledby="edituser-dialog-title"
        fullWidth="true"
        maxWidth="sm"
        open={openValidateClaim}
      >
        <CustomDialogTitle id="customized-dialog-title" onClose={handleClickValidateClaimClose}>
          Validation de la réclamation
        </CustomDialogTitle>
        <DialogContent dividers>
          <ValidateClaimDialog
            claim={currentClaim}
            closeModal={handleClickValidateClaimClose}
            refetch={refetch}
          />
        </DialogContent>
        <DialogActions></DialogActions>
      </Dialog>
    </>
  );
};

export default RTClaims;
